import { get } from "radash";
import { Vendor } from "@prisma/client";
import {
  Conditions,
  EntryWithRelations,
  Operators,
  ProjectRule,
  ProjectWithRelations,
  Properties,
  QualifierObj,
  Ruleset,
} from "./_types";
import { toCents } from "../utils/to-cents.utils";
import {
  getCreditPercentageValue,
  convertLegacyCreditPercentage,
} from "../utils/credit-percentage.utils";

const _truncateQualifyingReason = (reason: string) => {
  return reason.length > 100 ? reason.slice(0, 100) + "..." : reason;
};

// Legacy vendor URL fields that count as having the doc on file even when no
// document association exists (only W9 and business license have one).
// String literals rather than DocumentTypes.* on purpose: this map is built at
// module load, and the package must be importable before `prisma generate` has
// run (the jobs Docker build imports it to copy the schema out) — generated
// enums don't exist yet at that point.
const LEGACY_DOC_URL_FIELDS: Partial<
  Record<string, "businessLicenseUrl" | "w9Url">
> = {
  BUSINESS_LICENSE: "businessLicenseUrl",
  W9: "w9Url",
};

const _vendorHasDoc = (vendor: VendorDocInfo, docType: string): boolean => {
  if ((vendor.docTypesOnFile ?? []).includes(docType)) {
    return true;
  }
  const urlField = LEGACY_DOC_URL_FIELDS[docType];
  return !!(urlField && vendor[urlField]);
};

export type VendorDocInfo = Pick<
  Vendor,
  "docTypesOnFile" | "businessLicenseUrl" | "w9Url"
>;

// Shared by the vendor.hasRequiredDocs qualifier and UI surfaces that show
// vendor doc readiness, so both agree on what counts as "on file" (including
// the legacy URL fields).
export const getMissingVendorDocs = (
  vendor: VendorDocInfo,
  requiredVendorDocs: string[],
): string[] =>
  requiredVendorDocs.filter((docType) => !_vendorHasDoc(vendor, docType));

// vendor.hasRequiredDocs is derived rather than read off the entry: true when
// the vendor has every doc type the program's locality requires. With no
// program context (requiredVendorDocs undefined) the value is unknown, so the
// qualifier never matches — same as any other missing vendor data.
export const getQualifierActualValue = (
  entry: Partial<EntryWithRelations>,
  propertyName: QualifierObj["propertyName"],
  requiredVendorDocs?: string[],
): string | null => {
  if (propertyName === Properties.VENDOR_HAS_REQUIRED_DOCS) {
    if (!requiredVendorDocs) {
      return null;
    }
    const vendor = (entry as { vendor?: Vendor | null }).vendor;
    const hasAllDocs = requiredVendorDocs.every(
      (docType) => !!vendor && _vendorHasDoc(vendor, docType),
    );
    return String(hasAllDocs);
  }
  return get(entry, propertyName)?.toString().toLowerCase().trim() ?? null;
};

// Helper function to parse dates robustly
export const parseQualifierDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;

  // Try parsing with Date constructor first (handles ISO 8601 and many common formats)
  let date = new Date(dateStr);

  // If invalid, try some common formats manually
  if (isNaN(date.getTime())) {
    // Try parsing MM/DD/YYYY or MM-DD-YYYY
    const usFormat = dateStr.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (usFormat) {
      date = new Date(
        parseInt(usFormat[3]),
        parseInt(usFormat[1]) - 1,
        parseInt(usFormat[2]),
      );
    }

    // Try parsing DD/MM/YYYY or DD-MM-YYYY
    if (isNaN(date.getTime())) {
      const euFormat = dateStr.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
      if (euFormat) {
        date = new Date(
          parseInt(euFormat[3]),
          parseInt(euFormat[2]) - 1,
          parseInt(euFormat[1]),
        );
      }
    }

    // Try parsing YYYY/MM/DD or YYYY-MM-DD
    if (isNaN(date.getTime())) {
      const isoFormat = dateStr.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
      if (isoFormat) {
        date = new Date(
          parseInt(isoFormat[1]),
          parseInt(isoFormat[2]) - 1,
          parseInt(isoFormat[3]),
        );
      }
    }
  }

  return isNaN(date.getTime()) ? null : date;
};

// The raw value-comparison primitive, shared by the qualifying-reasons reducer
// below and the explain (checklist) path so both agree on match semantics.
// matchedValues carries the configured values that matched: all of them for a
// satisfied AND, the matching subset for OR.
export const matchQualifierValues = (
  currentValue: string | null,
  values: Array<string | number | boolean>,
  operator: Operators,
  modifier: Conditions,
): { matched: boolean; matchedValues: Array<string | number | boolean> } => {
  switch (operator) {
    case Operators.EQUALS: {
      if (modifier === Conditions.AND) {
        const matched = values.every(
          (val) => val.toString().toLowerCase().trim() == currentValue,
        );
        return { matched, matchedValues: matched ? values : [] };
      }
      const matchedValues = values.filter(
        (val) => val.toString().toLowerCase().trim() == currentValue,
      );
      return { matched: matchedValues.length > 0, matchedValues };
    }

    case Operators.IS_BEFORE:
    case Operators.IS_AFTER: {
      const currentDate = currentValue
        ? parseQualifierDate(currentValue)
        : null;
      if (!currentDate) {
        return { matched: false, matchedValues: [] };
      }
      const compares = (val: string | number | boolean): boolean => {
        const compareDate = parseQualifierDate(val.toString());
        if (!compareDate) return false;
        return operator === Operators.IS_BEFORE
          ? currentDate < compareDate
          : currentDate > compareDate;
      };
      if (modifier === Conditions.AND) {
        const matched = values.every(compares);
        return { matched, matchedValues: matched ? values : [] };
      }
      const matchedValues = values.filter(compares);
      return { matched: matchedValues.length > 0, matchedValues };
    }

    case Operators.INCLUDES:
    default: {
      if (modifier === Conditions.AND) {
        const matched = values.every((val) =>
          currentValue?.includes(val.toString().toLowerCase().trim()),
        );
        return { matched, matchedValues: matched ? values : [] };
      }
      const matchedValues = values.filter((val) =>
        currentValue?.includes(val.toString().toLowerCase().trim()),
      );
      return { matched: matchedValues.length > 0, matchedValues };
    }
  }
};

const _makeDetectorRunner =
  (entry: Partial<EntryWithRelations>, requiredVendorDocs?: string[]) =>
  (acc: string[], qualifier: QualifierObj): string[] => {
    const { propertyName, operator, modifier, additionalQualifiers } =
      qualifier;
    // Account codes may or may not carry hyphen delimiters depending on the
    // source ("23-01" vs "2301"), so both sides compare hyphen-free.
    const isAccountCode = propertyName === Properties.ACCOUNT_CODE;
    const values = isAccountCode
      ? qualifier.values.map((val) => val.toString().replace(/-/g, ""))
      : qualifier.values;
    const rawCurrentValue = getQualifierActualValue(
      entry,
      propertyName,
      requiredVendorDocs,
    );
    const currentValue =
      isAccountCode && rawCurrentValue
        ? rawCurrentValue.replace(/-/g, "")
        : rawCurrentValue;

    const { matched, matchedValues } = matchQualifierValues(
      currentValue,
      values,
      operator,
      modifier,
    );

    if (additionalQualifiers && additionalQualifiers.length > 0) {
      // If parent doesn't match, return early
      if (!matched) {
        return acc;
      }

      // Now check nested qualifiers
      const subQualifiers = additionalQualifiers.reduce(
        _makeDetectorRunner(entry, requiredVendorDocs),
        [],
      );

      // Both parent and ALL nested qualifiers must match
      if (subQualifiers.length === additionalQualifiers.length) {
        const description = `${propertyName} ${operator} ${values.join(
          ` ${modifier} `,
        )} and ${subQualifiers.join(" and ")}`;
        acc.push(_truncateQualifyingReason(description));
      }

      return acc;
    }

    if (!matched) {
      return acc;
    }

    // EQUALS reasons name the matched value alone; the other operators also
    // echo the entry's value in parens and list every configured value for
    // AND or only the matching ones for OR.
    if (operator === Operators.EQUALS) {
      const specificDescription =
        modifier === Conditions.AND
          ? `${propertyName} ${operator} ${currentValue}`
          : `${propertyName} ${operator} ${matchedValues[0]}`;
      acc.push(_truncateQualifyingReason(specificDescription));
      return acc;
    }

    const reasonValues = modifier === Conditions.AND ? values : matchedValues;
    const specificDescription = `${propertyName} (${currentValue}) ${operator} ${reasonValues.join(
      ` ${modifier} `,
    )}`;
    acc.push(_truncateQualifyingReason(specificDescription));
    return acc;
  };

export const makeDetector =
  ({
    ruleset: {
      id: rulesetId,
      name: rulesetName,
      qualifiers,
      actions,
      percentDeductionAllowed: entryPercentDeductionAllowed,
      maximumDeductionAllowed: entryMaximumDeductionAllowed,
    },
    projectRules,
    requiredVendorDocs,
  }: {
    ruleset: Ruleset;
    projectRules: Omit<ProjectRule, "minimumSpend">;
    // Doc types the program's locality requires vendors to have. Callers
    // without program context omit it, which leaves vendor.hasRequiredDocs
    // unknown (the qualifier never matches).
    requiredVendorDocs?: string[];
  }) =>
  (
    entry: Partial<EntryWithRelations>,
  ): {
    rulesetId: string;
    appliedRuleName: string;
    qualifyingReasons: string[];
    certainty: number;
    applyComputations: () => Partial<EntryWithRelations>;
  } => {
    const qualifyingReasons = qualifiers.reduce(
      _makeDetectorRunner(entry, requiredVendorDocs),
      [],
    );
    const certainty = qualifyingReasons.length / qualifiers.length;
    const { percentDeductionAllowed: projectPercentDeductionAllowed } =
      projectRules;
    const taxIncentiveAmount =
      toCents(entry.amount ?? 0) *
      ((entryPercentDeductionAllowed > 0
        ? entryPercentDeductionAllowed
        : getCreditPercentageValue(projectPercentDeductionAllowed)) /
        100);

    return {
      rulesetId,
      appliedRuleName: rulesetName,
      qualifyingReasons,
      certainty,
      applyComputations: () => {
        if (certainty === 0) {
          return entry;
        }

        const updatedEntry = actions.reduce(
          (entry, { numberOfQualifiers, updates }) => {
            // if qualifying reasons doesn't meet at least the minimum number of qualifiers required,
            // don't perform the action
            if (qualifyingReasons.length < numberOfQualifiers) {
              return entry;
            }

            return updates.reduce((entry, { fieldName, value }) => {
              // Skip updates with empty/undefined values
              if (value === "" || value === undefined || value === null) {
                return entry;
              }
              return {
                ...entry,
                [fieldName]: value,
              };
            }, entry);
          },
          {} as EntryWithRelations,
        );

        return {
          ...entry,
          ...updatedEntry,
          certainty,
          qualifyingReasons,
          rulesetId,
          appliedRuleName: rulesetName,
          taxIncentiveAmount:
            entryMaximumDeductionAllowed > 0 &&
            taxIncentiveAmount > toCents(entryMaximumDeductionAllowed)
              ? toCents(entryMaximumDeductionAllowed)
              : taxIncentiveAmount,
        };
      },
    };
  };

export const detectEntryQualifiers =
  (project: ProjectWithRelations) => (entry: EntryWithRelations) => {
    const programs = (project.incentiveRules?.programs as any[]) ?? [];

    // If programs array exists, use multi-program processing
    if (programs.length > 0) {
      let totalIncentiveAmount = 0;
      const allQualifyingReasons: string[] = [];
      let maxCertainty = 0;
      let matchedRulesetId: string | null = null;
      let matchedAppliedRuleName: string | null = null;
      let matchedTaxIncentiveStatus: string | null = null;
      let matchedFf1: string | null = null;
      let matchedFf2: string | null = null;

      // Process each program independently
      for (const program of programs) {
        const _detectors = (program.rules || []).map((rule: any) => {
          return makeDetector({
            ruleset: rule as Ruleset,
            projectRules: {
              percentDeductionAllowed: convertLegacyCreditPercentage(
                program.percentDeductionAllowed ?? 100,
              ),
              maximumDeductionAllowed: program.maximumDeductionAllowed ?? 0,
            },
            // Programs without configured requirements pass [] (vacuously
            // satisfied), since the program context itself exists.
            requiredVendorDocs: program.requiredVendorDocs ?? [],
          });
        });

        // First-match wins within each program
        let programDetector;
        let ruleIndex = 0;
        for (const detector of _detectors) {
          const result = detector(entry);

          if (result.certainty > 0) {
            programDetector = result;
            break;
          }
          ruleIndex++;
        }

        // If this program matched, accumulate the incentives
        if (programDetector) {
          const programResult = programDetector.applyComputations();

          if (programResult.taxIncentiveAmount) {
            totalIncentiveAmount += programResult.taxIncentiveAmount;
          }
          if (programResult.qualifyingReasons) {
            allQualifyingReasons.push(...programResult.qualifyingReasons);
          }
          if (
            programResult.certainty &&
            programResult.certainty > maxCertainty
          ) {
            maxCertainty = programResult.certainty;
          }
          if (!matchedRulesetId && programResult.rulesetId) {
            matchedRulesetId = programResult.rulesetId;
          }
          // Capture appliedRuleName from the first matching program
          if (!matchedAppliedRuleName && programResult.appliedRuleName) {
            matchedAppliedRuleName = programResult.appliedRuleName;
          }
          // Capture taxIncentiveStatus from the first matching program
          if (!matchedTaxIncentiveStatus && programResult.taxIncentiveStatus) {
            matchedTaxIncentiveStatus = programResult.taxIncentiveStatus;
          }
          // Capture FF1 and FF2 from the first matching program
          if (!matchedFf1 && programResult.ff1) {
            matchedFf1 = programResult.ff1;
          }
          if (!matchedFf2 && programResult.ff2) {
            matchedFf2 = programResult.ff2;
          }
        }
      }

      // Return entry with stacked incentives from all programs
      if (matchedRulesetId) {
        return {
          ...entry,
          taxIncentiveAmount: totalIncentiveAmount,
          qualifyingReasons: allQualifyingReasons,
          certainty: maxCertainty,
          rulesetId: matchedRulesetId,
          appliedRuleName: matchedAppliedRuleName,
          taxIncentiveStatus: matchedTaxIncentiveStatus,
          ...(matchedFf1 && { ff1: matchedFf1 }),
          ...(matchedFf2 && { ff2: matchedFf2 }),
        };
      }

      return entry;
    }

    const _detectors =
      (project.incentiveRules?.rules as any[] | null)?.map((rule: any) => {
        return makeDetector({
          ruleset: rule as Ruleset,
          projectRules: {
            percentDeductionAllowed: convertLegacyCreditPercentage(
              project.incentiveRules?.percentDeductionAllowed ?? 100,
            ),
            maximumDeductionAllowed:
              project.incentiveRules?.maximumDeductionAllowed ?? 0,
          },
        });
      }) ?? [];

    let positiveDetector;
    let ruleIndex = 0;

    for (const detector of _detectors) {
      const result = detector(entry);

      if (result.certainty > 0) {
        positiveDetector = result;
        break;
      }
      ruleIndex++;
    }

    if (positiveDetector) {
      const finalResult = positiveDetector.applyComputations();
      return finalResult;
    }

    return entry;
  };
